"use client";

import { format } from "date-fns";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Tag, User } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect } from "react";
import { ArticleContent } from "@/components/blogs/article-content";
import { RecentlyViewedBlogs } from "@/components/blogs/recently-viewed-blogs";
import { RelatedBlogs } from "@/components/blogs/related-blogs";
import { ShareButtons } from "@/components/blogs/share-buttons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { generateArticleSchema, JsonLd, siteConfig } from "@/lib/seo";
import type { Article, ArticleSummary } from "@/lib/shopify/types";
import { useBlogStore } from "@/lib/store/blog-store";

interface ArticleViewProps {
	article: Article;
	relatedArticles?: ArticleSummary[];
}

const fadeIn = {
	hidden: { opacity: 0, y: 20 },
	visible: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.5, ease: "easeOut" as const },
	},
};

const stagger = {
	hidden: {},
	visible: {
		transition: {
			staggerChildren: 0.1,
		},
	},
};

export function ArticleView({
	article,
	relatedArticles = [],
}: ArticleViewProps) {
	const addRecentlyViewed = useBlogStore((s) => s.addRecentlyViewed);
	const setSelectedTag = useBlogStore((s) => s.setSelectedTag);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Intentional dependency on ID only
	useEffect(() => {
		addRecentlyViewed({
			id: article.id,
			title: article.title,
			handle: article.handle,
			excerpt: article.excerpt,
			publishedAt: article.publishedAt,
			image: article.image,
			author: article.author,
			tags: article.tags,
			blog: article.blog,
		});
	}, [article.id]);

	const articleUrl =
		typeof window !== "undefined"
			? window.location.href
			: `${siteConfig.url}/blog/${article.handle}`;

	return (
		<motion.article
			initial="hidden"
			animate="visible"
			variants={stagger}
			className="max-w-4xl mx-auto px-4 md:px-6"
		>
			<motion.header variants={fadeIn} className="mb-8 mt-24 md:mt-32">
				{article.blog?.title && (
					<Badge variant="secondary" className="mb-4">
						{article.blog.title}
					</Badge>
				)}
				<h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
					{article.title}
				</h1>

				<div className="flex flex-wrap items-center gap-4 mt-6 text-muted-foreground">
					{article.author?.name && (
						<div className="flex items-center gap-2">
							<div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
								<User className="h-4 w-4 text-primary" />
							</div>
							<div>
								<span className="font-medium text-foreground">
									{article.author.name}
								</span>
								{article.author.email && (
									<span className="block text-xs text-muted-foreground">
										{article.author.email}
									</span>
								)}
							</div>
						</div>
					)}
					<div className="flex items-center gap-2">
						<Calendar className="h-4 w-4" />
						<span>{format(new Date(article.publishedAt), "MMMM d, yyyy")}</span>
					</div>
				</div>

				{article.tags && article.tags.length > 0 && (
					<div className="flex flex-wrap items-center gap-2 mt-4">
						<Tag className="h-4 w-4 text-muted-foreground" />
						{article.tags.map((tag) => (
							<Link
								key={tag}
								href={`/blog?tag=${encodeURIComponent(tag)}`}
								onClick={() => setSelectedTag(tag)}
							>
								<Badge
									variant="outline"
									className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
								>
									{tag}
								</Badge>
							</Link>
						))}
					</div>
				)}
			</motion.header>

			{article.image && (
				<motion.div
					variants={fadeIn}
					className="relative aspect-video rounded-2xl overflow-hidden mb-10"
				>
					<Image
						src={article.image.url}
						alt={article.image.altText || article.title}
						fill
						className="object-cover"
						sizes="(max-width: 896px) 100vw, 896px"
						priority
					/>
				</motion.div>
			)}

			<motion.div variants={fadeIn} className="mb-8">
				<ShareButtons
					url={articleUrl}
					title={article.title}
					description={article.excerpt || undefined}
				/>
			</motion.div>

			<ArticleContent html={article.contentHtml} />

			{article.author?.name && (
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.5 }}
					className="mt-12 p-6 bg-muted/50 rounded-xl border"
				>
					<div className="flex items-center gap-4">
						<div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
							<User className="h-7 w-7 text-primary" />
						</div>
						<div>
							<p className="text-sm text-muted-foreground uppercase tracking-wide font-medium">
								Written by
							</p>
							<p className="text-lg font-bold">{article.author.name}</p>
							{article.author.email && (
								<p className="text-sm text-muted-foreground">
									{article.author.email}
								</p>
							)}
						</div>
					</div>
				</motion.div>
			)}

			<motion.div
				initial={{ opacity: 0, y: 20 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true }}
				transition={{ duration: 0.5 }}
				className="mt-10 pt-8 border-t"
			>
				<ShareButtons
					url={articleUrl}
					title={article.title}
					description={article.excerpt || undefined}
				/>
			</motion.div>

			<motion.div
				initial={{ opacity: 0, y: 20 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true }}
				className="mt-16 pt-8 pb-16 border-t"
			>
				<Button variant="outline" className="gap-2" asChild>
					<Link href="/blog">
						<ArrowLeft className="h-4 w-4" />
						Back to all articles
					</Link>
				</Button>
			</motion.div>

			<div className="pt-8 pb-20 border-t">
				<RelatedBlogs
					articles={relatedArticles}
					currentArticleId={article.id}
					title="You May Also Like"
				/>

				<RecentlyViewedBlogs currentArticleId={article.id} />
			</div>

			<JsonLd data={generateArticleSchema(article)} />
		</motion.article>
	);
}
